import { useState, useEffect } from "react";
import { accountApi } from "../services/api";
import logger from "../utils/logger";

/**
 * Custom hook for fetching and caching user plan information.
 *
 * Manages fetching the user's plan from the API with automatic sessionStorage caching
 * and TTL validation. Useful for determining which features (like branding customization)
 * are available based on the user's subscription tier.
 *
 * CACHE STRATEGY:
 * - Cached in sessionStorage to persist across component remounts within a session
 * - 5-minute TTL allows users to see plan upgrades within the session
 * - Cache key: "userPlanCache" + "userPlanCacheTime"
 * - Automatic cleanup: Returns cleanup function that aborts in-flight requests
 *
 * @returns {Object} Hook return object
 *   @returns {Object|null} plan - User's plan object (id, name, allowBrandingCustomization, etc.)
 *   @returns {boolean} loading - True while fetching plan from API
 *   @returns {string|null} error - Error message if plan fetch failed
 *
 * @example
 * const { plan, loading, error } = useUserPlan();
 *
 * if (loading) return <Spinner />;
 * if (error) return <ErrorNotice message={error} />;
 * if (!plan?.allowBrandingCustomization) {
 *   return <UpgradePrompt />;
 * }
 * return <BrandingForm />;
 */
export function useUserPlan() {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();

    const fetchPlan = async () => {
      try {
        // Check sessionStorage cache first
        const cached = sessionStorage.getItem("userPlanCache");
        const cachedTime = sessionStorage.getItem("userPlanCacheTime");
        const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

        const now = Date.now();
        const isCacheValid =
          cached &&
          cachedTime &&
          now - Number.parseInt(cachedTime, 10) < CACHE_TTL_MS;

        if (isCacheValid) {
          const cachedPlan = JSON.parse(cached);
          setPlan(cachedPlan);
          setLoading(false);
          return;
        }

        // Cache expired or missing—fetch fresh data
        setLoading(true);
        setError(null);
        const response = await accountApi.getCurrentUser({
          signal: controller.signal,
        });

        // accountApi.getCurrentUser returns the unwrapped inner payload
        // { user, plan } — the apiClient response interceptor already strips
        // the outer { status, data } envelope before callers see it.
        const plan = response?.plan ?? null;
        // Cache regardless of value (including null) to prevent re-fetches on remount
        sessionStorage.setItem("userPlanCache", JSON.stringify(plan));
        sessionStorage.setItem("userPlanCacheTime", String(Date.now()));
        setPlan(plan);
        setLoading(false);
      } catch (err) {
        if (err.name !== "AbortError" && err.code !== "ERR_CANCELED") {
          logger.warn("Failed to fetch user plan", { error: err.message });
          setError(
            "Could not load your plan. Some features may be unavailable.",
          );
          setLoading(false);
        }
      }
    };

    fetchPlan();

    // Cleanup: abort in-flight requests on unmount
    return () => controller.abort();
  }, []);

  return { plan, loading, error };
}

export default useUserPlan;
